import {ViewedFileProgress} from './ViewedFileProgress'
import {OpenAlertsPanelButton} from './OpenAlertsPanelButton'
import {ReviewMenuButton} from './ReviewMenuButton'
import {DiffViewSettings} from '@github-ui/diff-view-settings'
import type {FilesPageNotices, UserNotice, FilesRoutePayload, PageLimits} from '../../page-data/payloads/files'
import {RefreshButton} from './RefreshButton'
import {Stack, Tooltip, Button, CounterLabel} from '@primer/react'
import {DiscardBatchConfirmationDialog} from '@github-ui/diff-lines/discard-batch-confirmation-dialog'
import type {SuggestionDetails, SuggestionIdentifier} from '@github-ui/diff-lines/conversation-types'
import {ChangesSelector} from '../diff-filtering/ChangesSelector'
import {useState, useCallback, useRef} from 'react'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {PullRequestStateLabel} from '../PullRequestStateLabel'
import {clsx} from 'clsx'
import styles from './PullRequestFilesToolbar.module.css'
import {PullRequestHeaderSummary} from '../PullRequestHeaderSummary'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import {useHeaderLiveUpdates} from '../../hooks/use-header-live-updates'
import type {AliveChannels, MergeStatusButtonData} from '../../page-data/payloads/header'
import {MergeMethodContextProvider, MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {
  useComparisonInfo,
  useIsSingleFileMode,
  useIsSuggestionBatchPending,
  useSuggestionBatch,
  useSuggestionBatchActions,
  useAllMarkers,
  usePullRequestBasePath,
  useShowChangeGroupsPreference,
} from '../../stores/selectors'
import {useUpdateWhitespacePreferenceMutation} from '../../mutations/use-update-whitespace-preference-mutation'
import {ReviewNavigationButtonGroup} from './ReviewNavigationButtonGroup'
import {ToolbarMoreMenu} from './ToolbarMoreMenu'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ChangeGroupsPreferences} from './ChangeGroupsPreferences'
import {ApplySuggestionDialog} from '@github-ui/diff-lines/apply-suggestion-dialog'
import {useApplyBatchedSuggestionsMutation} from '../../mutations/use-apply-batched-suggestions-mutation'
import {getSuggestionDetails} from '../../helpers/get-suggestion-details'
import {saveActiveBatchToStorage, clearActiveBatchFromStorage} from '../../helpers/suggestion-batch-storage'
import {useUpdateShowChangeGroupsPreferenceMutation} from '../../mutations/use-update-show-change-groups-preference-mutation'
import {OpenAgentsPanelButton} from './OpenAgentsPanelButton'
import {OpenOverviewPanelButton} from './OpenOverviewPanelButton'
import {useIsDockedPanelOpen, useDockedPanelActions} from '../../contexts/DockedPanelContext'
import {useMergeStatusButtonData} from '../../hooks/use-merge-status-button-data'
import {OpenCommentsPanelButton} from './OpenCommentsPanelButton'

export type PullRequestFilesToolbarProps = {
  changeGroupsPreferenceAvailable: boolean
  commits: FilesRoutePayload['commits']
  currentUserLogin?: string
  isFileTreeExpanded?: boolean
  lastReviewOid?: string
  mergeStatusButtonData?: MergeStatusButtonData
  pageLimits: PageLimits
  pullRequest: FilesRoutePayload['pullRequest']
  aliveChannels: AliveChannels
  repository: FilesRoutePayload['repository']
  shouldShowViewedFilesCount: boolean
  totalFilesCount: number
  treeToggleElement?: React.ReactElement
  userNotices?: Array<UserNotice<FilesPageNotices>>
}

export function LivePullRequestFilesToolbar({
  repository,
  pullRequest: initialPullRequest,
  userNotices,
  ...pullRequestFilesToolbarProps
}: Omit<PullRequestFilesToolbarProps, 'aliveChannels' | 'mergeStatusButtonData'>) {
  const {
    data: {pullRequest, aliveChannels, mergeStatusButtonData},
  } = useHeaderPageData()
  useHeaderLiveUpdates(aliveChannels)

  const updatedPullRequest = {...initialPullRequest, ...pullRequest} as FilesRoutePayload['pullRequest']

  return (
    <PullRequestFilesToolbar
      repository={repository}
      pullRequest={updatedPullRequest}
      aliveChannels={aliveChannels}
      mergeStatusButtonData={mergeStatusButtonData}
      userNotices={userNotices}
      {...pullRequestFilesToolbarProps}
    />
  )
}

export function PullRequestFilesToolbar({
  changeGroupsPreferenceAvailable,
  commits,
  currentUserLogin,
  isFileTreeExpanded,
  lastReviewOid,
  mergeStatusButtonData,
  pageLimits,
  pullRequest,
  aliveChannels,
  repository,
  shouldShowViewedFilesCount,
  totalFilesCount,
  treeToggleElement,
  userNotices,
}: PullRequestFilesToolbarProps) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const batchSuggestedChangesEnabled = useFeatureFlag('batch_suggested_changes')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const robustSmallScreenSupport = useFeatureFlag('prx_robust_small_screen_support')
  const {mutate: updateWhitespace} = useUpdateWhitespacePreferenceMutation()
  const {clearSuggestionBatch, removeSuggestionFromBatch} = useSuggestionBatchActions()
  const {mutate: applyBatchedSuggestions} = useApplyBatchedSuggestionsMutation(
    pullRequest.pathName,
    pullRequest.comparison.headOid,
  )
  const {mutate: updateChangeGroupPreference} = useUpdateShowChangeGroupsPreferenceMutation()
  const showChangeGroups = useShowChangeGroupsPreference()
  const [isSticky, setIsSticky] = useState(false)
  const showDivider = shouldShowViewedFilesCount || !isFileTreeExpanded
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)

  const isSingleFileMode = useIsSingleFileMode()
  const isSuggestionBatchPending = useIsSuggestionBatchPending()
  const suggestionBatch = useSuggestionBatch()
  const markers = useAllMarkers()
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false)
  const pullRequestBasePath = usePullRequestBasePath()
  const comparisonInfo = useComparisonInfo()

  const [changesSelectorOpen, setChangesSelectorOpen] = useState(false)

  const suggestionDetails = getSuggestionDetails(markers, suggestionBatch.activeBatch)
  const suggestionsCount = suggestionDetails.length

  const onUpdateWhitespace = (updatedValue: boolean) => {
    updateWhitespace({hideWhitespace: updatedValue})
  }

  const sensorRef = useIntersectionObserver(entries => {
    if (entries[0]) {
      setIsSticky(!entries[0].isIntersecting)
    }
  })

  const handleRemoveSuggestion = useCallback(
    (suggestionId: SuggestionIdentifier) => {
      removeSuggestionFromBatch(suggestionId)
      saveActiveBatchToStorage(
        pullRequestBasePath,
        comparisonInfo.fullDiff.headOid,
        suggestionBatch.activeBatch.filter(
          id =>
            !(
              id.commentDatabaseId === suggestionId.commentDatabaseId &&
              id.suggestionIndex === suggestionId.suggestionIndex
            ),
        ),
      )
    },
    [removeSuggestionFromBatch, suggestionBatch, pullRequestBasePath, comparisonInfo],
  )

  const handleCommitBatch = useCallback(() => {
    setIsCommitDialogOpen(true)
  }, [])

  const handleCommitDialogClose = useCallback(() => {
    setIsCommitDialogOpen(false)
  }, [])

  const handleDiscardClick = useCallback(() => {
    setShowDiscardConfirmation(true)
  }, [])

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirmation(false)
  }, [])

  const handleDiscardBatch = useCallback(() => {
    clearSuggestionBatch()
    clearActiveBatchFromStorage(pullRequestBasePath, comparisonInfo.fullDiff.headOid)
    handleCommitDialogClose()
    setShowDiscardConfirmation(false)
  }, [clearSuggestionBatch, pullRequestBasePath, comparisonInfo, handleCommitDialogClose])

  const handleCommit = useCallback(
    (commitMessage: string, onError: (error: Error) => void, onCompleted: () => void) => {
      applyBatchedSuggestions(
        {message: commitMessage},
        {
          onSuccess: () => {
            handleCommitDialogClose()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error)
          },
        },
      )
    },
    [applyBatchedSuggestions, handleCommitDialogClose],
  )
  const onShowChangeGroupsChange = (newVal: boolean) => {
    updateChangeGroupPreference({showChangeGroups: newVal})
  }

  const suggestions = suggestionDetails.map(
    (detail): SuggestionDetails => ({
      commentDatabaseId: detail.commentDatabaseId,
      suggestionIndex: detail.suggestionIndex,
      path: detail.path,
      bodyHTML: detail.bodyHTML,
      diffEntries: detail.diffEntries,
    }),
  )

  const sidePanelOpen = useIsDockedPanelOpen()

  // Merge status button setup
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel('mergeStatus', mergeStatusButtonRef.current)
  }, [dockedPanelActions])

  const isCodingAgentLogsInPrEnabled = isFeatureEnabled('coding_agent_logs_in_pr')
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const renderAgentSidePanelButton = isCodingAgentLogsInPrEnabled

  return (
    <>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
      <Stack
        as="section"
        direction="horizontal"
        justify="space-between"
        gap="condensed"
        align="center"
        className={clsx(stickyStyles.stickyHeader, styles.toolbar, isSticky && styles['is-stuck'])}
        data-side-panel-open={sidePanelOpen}
      >
        <h2 className="sr-only">Pull Request Toolbar</h2>

        <Stack direction="horizontal" gap="condensed" align="center" className="min-width-0">
          <div>{treeToggleElement}</div>
          <div className={styles['show-when-stuck']}>
            <PullRequestStateLabel state={pullRequest.state} />
          </div>
          <div
            className={clsx(
              styles['hide-when-stuck-large'],
              robustSmallScreenSupport &&
                (isFileTreeExpanded
                  ? styles['hide-commit-selector-on-small']
                  : styles['hide-commit-selector-on-medium']),
            )}
          >
            <ChangesSelector
              commits={commits}
              lastReviewOid={lastReviewOid}
              ownerLogin={repository.ownerLogin}
              pullRequestNumber={pullRequest.number}
              repositoryName={repository.name}
            />
          </div>
          <Stack direction="vertical" gap="none" className={clsx(styles['show-when-stuck'], 'min-width-0')}>
            <div className={clsx('d-flex mb-n1', styles['pr-title-details'])}>
              <Tooltip type="label" direction="s" text={pullRequest.title}>
                <a href="#top" className="d-flex overflow-hidden fgColor-default">
                  <bdi className={clsx('f5 text-bold overflow-hidden no-wrap', styles['pr-sticky-title'])}>
                    {pullRequest.title}
                  </bdi>
                </a>
              </Tooltip>
              <span className="f5 text-normal pl-2 fgColor-muted d-inline">#{pullRequest.number}</span>
            </div>

            <div
              className={clsx(
                'f6 text-normal d-flex flex-items-center ml-n2',
                robustSmallScreenSupport &&
                  (isFileTreeExpanded
                    ? styles['hide-commit-selector-on-small']
                    : styles['hide-commit-selector-on-medium']),
              )}
            >
              <ChangesSelector
                commits={commits}
                lastReviewOid={lastReviewOid}
                ownerLogin={repository.ownerLogin}
                pullRequestNumber={pullRequest.number}
                repositoryName={repository.name}
                variant="condensed"
                open={changesSelectorOpen}
                onOpenChange={setChangesSelectorOpen}
              />
              <div className={clsx('d-none d-lg-flex flex-items-center min-width-0', styles['pr-title-details'])}>
                <div className="border-left mx-1 pr-1 d-block" style={{width: '1px', height: '16px'}} />
                <PullRequestHeaderSummary
                  author={pullRequest.author}
                  baseBranch={pullRequest.baseBranch}
                  baseRepositoryOwnerLogin={repository.ownerLogin}
                  baseRepositoryName={repository.name}
                  commitsCount={pullRequest.commitsCount}
                  headBranch={pullRequest.headBranch}
                  headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                  headRepositoryName={pullRequest.headRepositoryName}
                  isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                  mergedBy={pullRequest.mergedBy}
                  mergedByName={pullRequest.mergedByName}
                  state={pullRequest.state}
                  pullRequestId={pullRequest.id}
                  pullRequestNumber={pullRequest.number}
                  repositoryId={repository.id}
                />
              </div>
            </div>
          </Stack>
        </Stack>
        <Stack direction="horizontal" align="center">
          <RefreshButton aliveChannel={pullRequest.aliveChannel} />

          <div className={clsx('d-flex', styles['file-controls'])}>
            {shouldShowViewedFilesCount && (
              <ViewedFileProgress
                totalFilesCount={totalFilesCount}
                className={styles['hide-viewed-progress-on-small']}
              />
            )}

            {isSingleFileMode && totalFilesCount > 1 && (
              <div className={styles['file-controls-navigation']}>
                <ReviewNavigationButtonGroup userNotices={userNotices} />
              </div>
            )}
          </div>
          {showDivider && <div className={styles['file-controls-divider']} data-testid="file-controls-divider" />}
          {isSuggestionBatchPending && batchSuggestedChangesEnabled ? (
            <>
              <Button aria-label="Discard batch" variant="danger" size="small" onClick={handleDiscardClick}>
                Discard<span className={`d-none ${sidePanelOpen ? ' d-xl-inline' : ' d-lg-inline'}`}> batch</span>
              </Button>
              <Button aria-label="Commit suggestions" variant="primary" size="small" onClick={handleCommitBatch}>
                Commit<span className={`d-none ${sidePanelOpen ? ' d-xl-inline' : ' d-lg-inline'}`}> suggestions</span>{' '}
                <CounterLabel className="ml-1 color-fg-inherit">{suggestionsCount}</CounterLabel>
              </Button>
            </>
          ) : (
            <>
              {validatedMergeData && isSticky && !sidePanelOpen && (
                <div className={styles['show-on-xlarge']}>
                  <MergeMethodContextProvider defaultMergeMethod={validatedMergeData.defaultMergeMethod}>
                    <MergeStatusButtonWrapper
                      channels={validatedMergeData.channels}
                      headSha={validatedMergeData.headSha}
                      helpUrl={validatedMergeData.helpUrl}
                      viewerLogin={validatedMergeData.viewerLogin}
                      isDockedMode
                      onToggleDockedPanel={handleToggleDockedMergePanel}
                      mergeStatusButtonRef={mergeStatusButtonRef}
                      size="small"
                    />
                  </MergeMethodContextProvider>
                </div>
              )}
              {currentUserLogin && (
                <ReviewMenuButton
                  currentUserLogin={currentUserLogin}
                  pullRequest={pullRequest}
                  repository={repository}
                />
              )}
              <div className={robustSmallScreenSupport ? styles['container-show-on-sm'] : 'd-none d-sm-flex'}>
                <DiffViewSettings
                  invisible={false}
                  reloadOnWhitespaceChange
                  onUpdateWhitespace={onUpdateWhitespace}
                  defaultHideWhitespace={comparisonInfo.hideWhitespace}
                  small
                  userNotices={userNotices}
                  additionalPreferences={
                    <ChangeGroupsPreferences
                      changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                      showChangeGroups={showChangeGroups}
                      onShowChangeGroupsChange={onShowChangeGroupsChange}
                    />
                  }
                />
              </div>
            </>
          )}
          <div className={styles['file-controls-divider']} />
          <div className="d-flex gap-1">
            {!showUpdatedHeader && <OpenOverviewPanelButton aliveChannel={pullRequest.aliveChannel} />}
            {renderAgentSidePanelButton && (
              <div className={robustSmallScreenSupport ? styles['container-show-on-sm'] : 'd-none d-sm-flex'}>
                <OpenAgentsPanelButton repository={repository} pullRequestNumber={pullRequest.number} />
              </div>
            )}
            {!showUpdatedHeader && (
              <div className={robustSmallScreenSupport ? styles['container-show-on-sm'] : 'd-none d-sm-flex'}>
                <OpenCommentsPanelButton pageLimits={pageLimits} aliveChannels={aliveChannels} />
              </div>
            )}
            {!showUpdatedHeader && (
              <OpenAlertsPanelButton
                className="d-none d-sm-flex"
                hideIfNoAlerts
                pageLimits={pageLimits}
                endCommitOid={pullRequest.comparison.headOid}
              />
            )}
          </div>
          <div
            className={
              robustSmallScreenSupport
                ? isFileTreeExpanded
                  ? styles['show-more-menu-on-small']
                  : styles['show-more-menu-on-medium']
                : 'd-flex d-sm-none'
            }
          >
            <ToolbarMoreMenu
              pageLimits={pageLimits}
              pullRequest={pullRequest}
              aliveChannels={aliveChannels}
              hideWhitespace={comparisonInfo.hideWhitespace}
              onUpdateWhitespace={onUpdateWhitespace}
              showCommitSelector={() => setChangesSelectorOpen(true)}
              commits={commits}
              isFileTreeExpanded={isFileTreeExpanded}
              repository={repository}
              pullRequestNumber={pullRequest.number}
              additionalPreferences={
                <ChangeGroupsPreferences
                  changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                  showChangeGroups={showChangeGroups}
                  onShowChangeGroupsChange={onShowChangeGroupsChange}
                />
              }
            />
          </div>
        </Stack>
      </Stack>
      {isCommitDialogOpen && suggestions.length > 0 && (
        <ApplySuggestionDialog
          authorLogins={[]}
          batchSize={suggestionBatch.activeBatch.length}
          onClose={handleCommitDialogClose}
          onCommit={handleCommit}
          defaultCommitMessage="Apply suggestions from code review"
          suggestions={suggestions}
          onRemoveSuggestion={handleRemoveSuggestion}
          onDiscardBatch={handleDiscardBatch}
        />
      )}
      <DiscardBatchConfirmationDialog
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscardBatch}
        onCancel={handleDiscardCancel}
        suggestionsCount={suggestionsCount}
      />
    </>
  )
}
